import { useRef, useState, type ReactNode } from 'react';
import {
  AGENT_IMAGE_EXTENSIONS,
  AGENT_MESSAGE_MAX_CHARS,
  TABLE_EXTENSIONS,
  loadOf,
  toWireDate,
  type DashboardView,
  type MyWeek,
  type MyWeekDay,
  type MyWeekDayItem,
  type ResourceLoad,
} from '@forecastify/shared';
import { Button, Icon, Skeleton, Textarea, Tooltip, type IconName } from '@forecastify/ui';
import { useDuration } from '../../duration';
import { useI18n, useT, type Translate } from '../../i18n';
import { ListHeader, usePageHeader } from '../page-header';
import { ComposerGlow } from '../agent/composer-glow';
import { SectionSkeletonRows, StatusDot } from './home-sections';
import mark from '../../assets/forecastify-mark.svg';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Kezdőlap — the reworked homescreen (16 Oct 2026).                         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `docs/mockup/Forecastify - homescreen.html`, `homeV2`. The page leads with the Agent and keeps
 * four small cards under it:
 *
 * | Card        | Shown                         | Body                                              |
 * | ----------- | ----------------------------- | ------------------------------------------------- |
 * | (greeting)  | always; composer with Agent   | the mark, the greeting, a question box            |
 * | Időlogok    | always                        | four figures for the WEEK: ma, hét, hátra, hiány  |
 * | Allokációim | the person is resource-planned| a column per working day, a row per line item     |
 * | Szabadság   | something pending or upcoming | *elbírálás alatt*, *közelgő távollétek*           |
 * | Túlóra      | something pending or recent   | *elbírálás alatt*, *elfogadva az elmúlt hónapban* |
 *
 * ⚠️⚠️ **WHAT THE REWORK REMOVED, ON THE PRODUCT OWNER'S CONFIRMATION:** the collapsible
 * sections and their pills, the week/month hours chart (Időrögzítés draws the period now), the
 * leave quota cards, the overtime figures and the Feladatok section.
 *
 * ⚠️ **What it kept out of the mockup:** its monospace figures (Inter and `tabular-nums` — one
 * family), its native textarea resize handle, and its *Vannak / Nincsenek* prototype switch.
 *
 * ⚠️ **Not one figure is computed here.** `/workspace/dashboard` and `/dashboards/my-week` answer
 * everything drawn; the screen formats and lays out.
 */

/** The resource grid's three — a day's verdict reads the same here as on Erőforrás. */
const LOAD_COLOR: Readonly<Record<ResourceLoad, string | undefined>> = {
  under: 'var(--load-under)',
  ok: 'var(--load-ok)',
  over: 'var(--load-over)',
  none: undefined,
};

/** Time's own `under` red — *Hiányos napok* means what the Logolás grid tints. */
const SHORT_DAY = 'var(--status-bad)';

/** The request lists' four status colours. */
const STATUS_COLOR: Readonly<Record<string, string>> = {
  pending: 'var(--status-warn)',
  approved: 'var(--status-ok)',
  rejected: 'var(--status-bad)',
  revoked: 'var(--status-idle)',
};

/** 29 Oct 2026 · an absence amber, a holiday blue — Időrögzítés's strip and every leave grid. */
const HOLIDAY_INK = 'var(--status-info-text)';
const HOLIDAY_TINT = 'color-mix(in oklch, var(--status-info) 12%, transparent)';
const LEAVE_INK = 'var(--leave-ink)';
const LEAVE_TINT = 'color-mix(in oklch, var(--leave) 14%, transparent)';

function statusWord(t: Translate, status: string): string {
  /* ⚠️ One `t()` per branch with a literal key. */
  if (status === 'approved') return t('leave.status.approved');
  if (status === 'rejected') return t('leave.status.rejected');
  if (status === 'revoked') return t('leave.status.revoked');
  return t('leave.status.pending');
}

export interface DashboardHandlers {
  readonly onGoTime: () => void;
  readonly onOpenRequest: (id: string) => void;
  readonly onOpenClaim: (id: string) => void;
  readonly onGoLeave: () => void;
  readonly onGoOvertime: () => void;
  /** Both halves of a line item's address (R-57); a row without them is not a link. */
  readonly onOpenLineItem: (contractIdentifier: string, lineItemIdentifier: string) => void;
  /** The question and its files, handed to the Agent (`agent-handoff.ts`). */
  readonly onAsk: (text: string, files: readonly File[]) => void;
}

export function DashboardScreen({
  view,
  week,
  agentOn,
  logsTime = true,
  greeting,
  handlers,
  loading = false,
}: {
  readonly view: DashboardView;
  /** `null` until it arrives, and on a failure — the card is then simply not drawn. */
  readonly week: MyWeek | null;
  /** The workspace has the Agent — the composer and its caption are drawn only then. */
  readonly agentOn: boolean;
  /** 26 Oct 2026 · whether this person logs time — the *Időlogok* card is theirs only. */
  readonly logsTime?: boolean;
  /** Already translated: the Agent's own greeting, with the reader's name. */
  readonly greeting: string;
  readonly handlers: DashboardHandlers;
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
}): ReactNode {
  const { fmt } = useDuration();
  const t = useT();

  usePageHeader(() => <ListHeader title={t('title.home')} />, []);

  const hours = (value: string | null): string => (value === null ? '–' : fmt(Number(value)));

  const left = view.weekLeft === null ? null : Number(view.weekLeft);
  const leaveShown = view.pendingRows.length + view.upcomingRows.length > 0;
  const overtimeShown = view.overtimePendingRows.length + view.overtimeApprovedRows.length > 0;
  /* ⚠️ The mockup's `single`: one of the two alone takes the whole row. */
  const pairSpan = leaveShown !== overtimeShown ? 'lg:col-span-2' : '';

  /** The four figures — the mockup's `h2Stats`, all of them the current week's. */
  const stats: readonly {
    key: string;
    label: string;
    value: string;
    sub: string;
    color?: string;
  }[] = [
    {
      key: 'today',
      label: t('home.stat.today'),
      value: hours(view.todayHours),
      sub: view.todayPlanned === null ? '' : `/ ${hours(view.todayPlanned)}`,
    },
    {
      key: 'week',
      label: t('home.stat.thisWeek'),
      value: hours(view.weekHoursLogged),
      sub: view.weekPlanned === null ? t('home.stat.noSchedule') : `/ ${hours(view.weekPlanned)}`,
    },
    {
      key: 'left',
      label: left !== null && left < 0 ? t('home.stat.weekOver') : t('home.stat.weekLeft'),
      value: left === null ? '–' : fmt(Math.abs(left)),
      sub:
        left === null || left < 0
          ? ''
          : left === 0
            ? t('home.stat.onPlan')
            : t('home.stat.ofWeeklyPlan'),
    },
    {
      key: 'short',
      label: t('home.stat.missingDays'),
      value: String(view.weekMissingDays),
      sub: t('home.stat.thisWeekLower'),
      ...(view.weekMissingDays === 0 ? {} : { color: SHORT_DAY }),
    },
  ];

  return (
    <div data-screen="home" className="relative isolate flex-1 overflow-y-auto">
      {loading ? (
        <div role="status" aria-live="polite" className="sr-only">
          {t('state.loading')}
        </div>
      ) : null}
      {agentOn ? <ComposerGlow around="page" /> : null}

      <div className="mx-auto flex w-full max-w-[1080px] flex-col gap-10 px-5 pt-14 pb-10">
        {/* ── the greeting and the question ────────────────────────────────────────────────── */}
        <div data-tour="home-agent" className="w-full">
          <h1 className="flex items-center justify-center gap-3 text-center text-[26px] leading-tight font-medium tracking-tight text-balance">
            <img src={mark} alt="" className="h-[30px] w-auto shrink-0" />
            <span>{greeting}</span>
          </h1>
          {agentOn ? <HomeComposer onAsk={handlers.onAsk} /> : null}
        </div>

        <div className="flex flex-col gap-2.5">
          {agentOn ? (
            <div className="text-muted-foreground flex items-center gap-1.5 px-1 text-[12px]">
              <Icon name="sparkles" size={13} className="shrink-0" />
              <span>{t('home.ask.caption')}</span>
            </div>
          ) : null}

          <div className="grid grid-cols-[minmax(0,1fr)] gap-3 lg:grid-cols-2">
            {/* ── Időlogok ──────────────────────────────────────────────────────────────────── */}
            {logsTime ? (
              <Card
                tour="home-log"
                icon="clock"
                title={t('home.section.log')}
                className="lg:col-span-2"
                action={
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={handlers.onGoTime}
                  >
                    <Icon name="plus" size={14} />
                    {t('home.action.logTime')}
                  </Button>
                }
              >
                <div className="grid grid-cols-2 border-t md:grid-cols-4">
                  {stats.map((stat) => (
                    <div
                      key={stat.key}
                      className="border-r border-b px-4 py-3 even:border-r-0 md:border-b-0 md:even:border-r md:last:border-r-0 [&:nth-child(n+3)]:border-b-0"
                    >
                      <div className="text-muted-foreground truncate text-[11.5px]">
                        {stat.label}
                      </div>
                      {loading ? (
                        <div className="mt-1 flex h-[28.5px] items-center">
                          <Skeleton width="56px" className="h-4" />
                        </div>
                      ) : (
                        <div className="mt-1 flex items-baseline gap-1.5">
                          <span
                            className="text-[19px] font-medium tracking-tight tabular-nums"
                            style={stat.color === undefined ? undefined : { color: stat.color }}
                          >
                            {stat.value}
                          </span>
                          {stat.sub === '' ? null : (
                            <span className="text-muted-foreground truncate text-[12px] tabular-nums">
                              {stat.sub}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            ) : null}

            {/* ── Allokációim ───────────────────────────────────────────────────────────────── */}
            {/* ⚠️ Hidden for somebody the workspace does not plan — `allocShow: resPlanned(ME)`. */}
            {week?.planned !== true ? null : (
              <AllocationsCard week={week} onOpenLineItem={handlers.onOpenLineItem} />
            )}

            {/* ── Szabadság ─────────────────────────────────────────────────────────────────── */}
            {loading ? (
              <Card tour="home-requests" icon="sun" title={t('home.section.leave')} className="">
                <SectionSkeletonRows tall count={2} />
              </Card>
            ) : leaveShown ? (
              <Card
                tour="home-requests"
                icon="sun"
                title={t('home.section.leave')}
                className={pairSpan}
                action={
                  <Button variant="outline" size="sm" onClick={handlers.onGoLeave}>
                    {t('home.requests.all')}
                  </Button>
                }
              >
                {view.pendingRows.length === 0 ? null : (
                  <Group label={t('home.leave.pending')}>
                    {view.pendingRows.map((row) => (
                      <RowButton
                        key={row.id}
                        onClick={() => {
                          handlers.onOpenRequest(row.id);
                        }}
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[12.5px] font-medium">
                            {row.leaveTypeName}
                          </span>
                          <span className="text-muted-foreground mt-0.5 block text-[11.5px] tabular-nums">
                            {row.fromDate === row.toDate
                              ? row.fromDate
                              : `${row.fromDate} – ${row.toDate}`}
                          </span>
                        </span>
                        <span className="text-[12px] tabular-nums">
                          {t('leave.amount.days', { days: row.days })}
                        </span>
                        <StatusDot
                          color={STATUS_COLOR[row.status] ?? 'var(--status-idle)'}
                          label={statusWord(t, row.status)}
                        />
                      </RowButton>
                    ))}
                  </Group>
                )}
                {view.upcomingRows.length === 0 ? null : (
                  <Group label={t('home.leave.upcoming')}>
                    {/* ⚠️ Five at most — the mockup's `slice(0, 5)`; the rest is a click away. */}
                    {view.upcomingRows.slice(0, 5).map((row) => (
                      <RowButton
                        key={row.id}
                        onClick={() => {
                          handlers.onOpenRequest(row.id);
                        }}
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[12.5px] font-medium">
                            {row.leaveTypeName}
                          </span>
                          <span className="text-muted-foreground mt-0.5 block text-[11.5px] tabular-nums">
                            {row.fromDate === row.toDate
                              ? row.fromDate
                              : `${row.fromDate} – ${row.toDate}`}
                          </span>
                        </span>
                        <span className="text-[12px] tabular-nums">
                          {t('leave.amount.days', { days: row.days })}
                        </span>
                      </RowButton>
                    ))}
                  </Group>
                )}
                <div className="h-1.5" />
              </Card>
            ) : null}

            {/* ── Túlóra ────────────────────────────────────────────────────────────────────── */}
            {loading || !overtimeShown ? null : (
              <Card
                tour="home-overtime"
                icon="timer"
                title={t('home.section.overtime')}
                className={pairSpan}
                action={
                  <Button variant="outline" size="sm" onClick={handlers.onGoOvertime}>
                    {t('home.requests.all')}
                  </Button>
                }
              >
                {view.overtimePendingRows.length === 0 ? null : (
                  <Group label={t('home.leave.pending')}>
                    {view.overtimePendingRows.map((row) => (
                      <ClaimRow
                        key={row.id}
                        row={row}
                        onOpen={() => {
                          handlers.onOpenClaim(row.id);
                        }}
                      />
                    ))}
                  </Group>
                )}
                {view.overtimeApprovedRows.length === 0 ? null : (
                  <Group label={t('home.overtime.approvedRecent')}>
                    {view.overtimeApprovedRows.map((row) => (
                      <ClaimRow
                        key={row.id}
                        row={row}
                        onOpen={() => {
                          handlers.onOpenClaim(row.id);
                        }}
                      />
                    ))}
                  </Group>
                )}
                <div className="h-1.5" />
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── the pieces ─────────────────────────────────────────────────────────────────────────────── */

/** One of the cards under the greeting: icon, title, an action on the right, then its body. */
function Card({
  tour,
  icon,
  title,
  action,
  className,
  children,
}: {
  readonly tour: string;
  readonly icon: IconName;
  readonly title: string;
  readonly action?: ReactNode;
  readonly className: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <div
      data-tour={tour}
      className={`bg-card text-card-foreground min-w-0 rounded-xl border shadow-sm ${className}`}
    >
      <div className="flex min-h-11 flex-wrap items-center gap-x-2.5 gap-y-1 px-4 py-2">
        <Icon name={icon} size={15} className="text-muted-foreground shrink-0" />
        <span className="text-[13px] font-medium">{title}</span>
        <div className="flex-1" />
        {action}
      </div>
      {children}
    </div>
  );
}

/** A labelled run of rows inside a card — *Elbírálás alatt*, *Közelgő távollétek*. */
function Group({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div>
      <div className="text-muted-foreground border-t px-4 pt-2.5 pb-1 text-[12px]">{label}</div>
      {children}
    </div>
  );
}

function RowButton({
  onClick,
  children,
}: {
  readonly onClick: () => void;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      className="hover:bg-muted/50 flex w-full items-center gap-3 px-4 py-2 text-left transition-colors"
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/** A claim: the day, its type (an em-dash until an approver prices it), the task and the hours. */
function ClaimRow({
  row,
  onOpen,
}: {
  readonly row: DashboardView['overtimePendingRows'][number];
  readonly onOpen: () => void;
}): ReactNode {
  const { fmt } = useDuration();
  const t = useT();
  return (
    <RowButton onClick={onOpen}>
      <span className="text-muted-foreground w-[78px] shrink-0 text-[11.5px] tabular-nums">
        {row.workedOn}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12.5px] font-medium">
          {row.overtimeTypeName ?? t('home.overtime.unpriced')}
        </span>
        <span className="text-muted-foreground mt-0.5 block text-[11px]">{row.taskKey ?? '–'}</span>
      </span>
      <span className="shrink-0 text-[12px] whitespace-nowrap tabular-nums">
        {/* ⚠️ The hours alone while it is unpriced — `0147`. */}
        {row.multiplier === null
          ? fmt(Number(row.hours))
          : `${fmt(Number(row.hours))} × ${Number(row.multiplier).toLocaleString()}`}
      </span>
    </RowButton>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The question box. The Agent's own composer, minus everything a thread has.│
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The files are held HERE until the question goes**, unlike the Agent's own composer, which
 * uploads on pick: there is no thread yet to upload to, and starting one for a file somebody may
 * remove again would leave an empty conversation in their history. They travel with the question
 * (`agent-handoff.ts`), and the Agent uploads them first.
 */
function HomeComposer({
  onAsk,
}: {
  readonly onAsk: (text: string, files: readonly File[]) => void;
}): ReactNode {
  const t = useT();
  const picker = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState('');
  const [files, setFiles] = useState<readonly File[]>([]);

  const canSend = draft.trim() !== '';
  const submit = (): void => {
    if (!canSend) return;
    onAsk(draft.trim(), files);
  };

  return (
    <div
      data-tour="home-composer"
      className="bg-card focus-within:border-ring focus-within:ring-ring/40 mt-6 rounded-2xl border p-3 shadow-sm transition-[color,box-shadow] focus-within:ring-[3px]"
    >
      <Textarea
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onKeyDown={(event) => {
          /* The Agent's own binding: Enter sends, Shift+Enter is a newline. */
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            submit();
          }
        }}
        /* ⚠️ A pasted screenshot attaches, as it does on the Agent — `agent-screen.tsx` argues it. */
        onPaste={(event) => {
          const pasted = [...event.clipboardData.files].filter((file) =>
            file.type.startsWith('image/'),
          );
          if (pasted.length === 0) return;
          event.preventDefault();
          setFiles((current) => [...current, ...pasted]);
        }}
        placeholder={t('agent.placeholder')}
        maxLength={AGENT_MESSAGE_MAX_CHARS}
        aria-label={t('agent.placeholder')}
        className="min-h-10 resize-none border-0 bg-transparent px-1 shadow-none focus-visible:ring-0"
      />
      <div className="mt-1 flex flex-wrap items-center gap-1">
        <input
          ref={picker}
          type="file"
          multiple
          accept={[...TABLE_EXTENSIONS, ...AGENT_IMAGE_EXTENSIONS].join(',')}
          className="hidden"
          onChange={(event) => {
            const chosen = [...(event.target.files ?? [])];
            /* Cleared, so picking the same file twice still fires `change`. */
            event.target.value = '';
            if (chosen.length > 0) setFiles((current) => [...current, ...chosen]);
          }}
        />
        <Button
          size="icon-sm"
          variant="ghost"
          aria-label={t('agent.attach')}
          title={t('agent.attachHint')}
          onClick={() => {
            picker.current?.click();
          }}
        >
          <Icon name="paperclip" size={14} />
        </Button>
        {files.map((file, index) => (
          <span
            key={`${file.name}:${String(index)}`}
            className="bg-muted flex max-w-[220px] items-center gap-1.5 rounded-md px-2 py-1 text-[11.5px]"
          >
            <Icon name="file-text" size={12} className="text-muted-foreground shrink-0" />
            <span className="truncate">{file.name}</span>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground shrink-0"
              aria-label={t('agent.files.remove', { name: file.name })}
              onClick={() => {
                setFiles((current) => current.filter((_, at) => at !== index));
              }}
            >
              <Icon name="x" size={12} />
            </button>
          </span>
        ))}
        <div className="flex-1" />
        <Button size="icon-sm" disabled={!canSend} aria-label={t('agent.send')} onClick={submit}>
          <Icon name="arrow-right" size={15} />
        </Button>
      </div>
    </div>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Allokációim — the week as columns, the line items as rows.                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The mockup's `h2AllocDays`: Monday to Friday, and a weekend day only when the calendar makes it
 * a working one. Each column heads with *allocated / capacity* in the verdict's colour; each row is
 * a line item, and its tooltip says the rest — that day, what was logged against it, the week,
 * the booking's window and spread, and any overtime rate.
 *
 * ⚠️ **Fixed to this week.** No arrows and no Napi/Heti: the home card answers *what am I on
 * this week*, and stepping through weeks is the Erőforrás dashboard's job.
 */
function AllocationsCard({
  week,
  onOpenLineItem,
}: {
  readonly week: MyWeek;
  readonly onOpenLineItem: DashboardHandlers['onOpenLineItem'];
}): ReactNode {
  const { fmt } = useDuration();
  const t = useT();
  const { locale } = useI18n();
  const shown = week.days.filter((day) => !day.weekend || day.workingDay);
  const total = loadOf(Number(week.allocated), Number(week.capacity));

  return (
    <Card
      tour="home-allocations"
      icon="layout-grid"
      title={t('home.section.allocations')}
      className="lg:col-span-2"
      action={
        <span
          className="text-[12.5px] font-medium tabular-nums"
          style={{ color: LOAD_COLOR[total] ?? 'var(--foreground)' }}
        >
          {`${fmt(Number(week.allocated))} / ${fmt(Number(week.capacity))}`}
        </span>
      }
    >
      <div className="overflow-x-auto border-t">
        <div
          className="grid"
          style={{
            gridTemplateColumns: `repeat(${String(shown.length)}, minmax(0, 1fr))`,
            minWidth: `${String(shown.length * 110)}px`,
          }}
        >
          {shown.map((day) => (
            <DayColumn key={day.date} day={day} locale={locale} onOpenLineItem={onOpenLineItem} />
          ))}
        </div>
      </div>
    </Card>
  );
}

function DayColumn({
  day,
  locale,
  onOpenLineItem,
}: {
  readonly day: MyWeekDay;
  readonly locale: string;
  readonly onOpenLineItem: DashboardHandlers['onOpenLineItem'];
}): ReactNode {
  const { fmt } = useDuration();
  const t = useT();
  const today = toWireDate(new Date()) === day.date;
  const allocated = Number(day.allocated);
  const capacity = Number(day.capacity);
  const verdict = loadOf(allocated, capacity);
  const date = new Date(`${day.date}T00:00:00Z`);
  const label = `${new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(date)} ${String(date.getUTCDate())}.`;

  /* What the head says when there is nothing to put over a capacity. */
  const off: { text: string; ink: string; tint?: string } | null =
    day.holidayName !== null
      ? { text: day.holidayName, ink: HOLIDAY_INK, tint: HOLIDAY_TINT }
      : day.leaveTypeName !== null
        ? { text: day.leaveTypeName, ink: LEAVE_INK, tint: LEAVE_TINT }
        : null;

  return (
    <div
      className={`flex min-w-0 flex-col border-r last:border-r-0 ${today ? 'bg-primary/[0.04]' : ''}`}
    >
      <div className="flex flex-col gap-0.5 border-b px-2.5 py-2">
        <span
          className={`text-[11.5px] whitespace-nowrap ${today ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}
        >
          {label}
        </span>
        <Tooltip
          asChild
          content={t('resource.cell.summary', {
            capacity: fmt(capacity),
            allocated: fmt(allocated),
          })}
        >
          <span
            className="text-[12px] font-medium whitespace-nowrap tabular-nums"
            style={{
              color:
                allocated > 0
                  ? (LOAD_COLOR[verdict] ?? 'var(--foreground)')
                  : off !== null
                    ? off.ink
                    : 'var(--muted-foreground)',
            }}
          >
            {capacity > 0 || allocated > 0
              ? `${fmt(allocated)} / ${fmt(capacity)}`
              : day.holidayName !== null
                ? t('home.alloc.holiday')
                : day.leaveTypeName !== null
                  ? t('home.alloc.leave')
                  : '–'}
          </span>
        </Tooltip>
      </div>
      <div className="flex min-h-[64px] flex-col gap-1 p-1.5">
        {day.items.map((item) => (
          <AllocationItem key={item.lineItemId} item={item} onOpenLineItem={onOpenLineItem} />
        ))}
        {day.items.length > 0 ? null : off !== null ? (
          <span
            className="rounded-md px-1.5 py-1 text-[11px] font-medium"
            style={{ color: off.ink, background: off.tint }}
          >
            {off.text}
          </span>
        ) : (
          <span className="text-muted-foreground/70 px-1.5 py-1 text-[11px]">
            {capacity > 0 ? t('home.alloc.none') : t('home.alloc.noCapacity')}
          </span>
        )}
      </div>
    </div>
  );
}

function AllocationItem({
  item,
  onOpenLineItem,
}: {
  readonly item: MyWeekDayItem;
  readonly onOpenLineItem: DashboardHandlers['onOpenLineItem'];
}): ReactNode {
  const { fmt } = useDuration();
  const t = useT();
  const priced = item.overtimeTypeName !== null;
  const rows: { label: string; value: string }[] = [
    {
      label: t('home.alloc.tip.day'),
      value: priced
        ? t('home.alloc.tip.billed', {
            hours: fmt(Number(item.hours)),
            billed: fmt(Number(item.billedHours)),
          })
        : fmt(Number(item.hours)),
    },
    { label: t('home.alloc.tip.logged'), value: fmt(Number(item.loggedHours)) },
    { label: t('home.alloc.tip.week'), value: fmt(Number(item.weekHours)) },
    {
      label: t('home.alloc.tip.period'),
      value:
        item.fromDate === null || item.toDate === null
          ? t('home.alloc.tip.periods', { count: item.allocationCount })
          : `${item.fromDate} – ${item.toDate}`,
    },
    {
      label: t('resource.item.distribution'),
      value:
        item.distribution === null
          ? '–'
          : item.distribution === 'even'
            ? t('resource.dist.even')
            : t('resource.dist.earliest'),
    },
    ...(priced
      ? [
          {
            label: t('resource.tip.overtime'),
            value: t('resource.item.overtime', {
              name: item.overtimeTypeName ?? '',
              multiplier: Number(item.multiplier).toLocaleString(),
            }),
          },
        ]
      : []),
  ];

  const tip = (
    <span className="block">
      <span className="flex items-start gap-2">
        <span
          aria-hidden
          className="mt-[5px] size-2 shrink-0 rounded-[3px]"
          style={{ background: item.projectColor ?? 'var(--muted-foreground)' }}
        />
        <span className="min-w-0">
          <span className="block text-[12.5px] leading-snug font-medium">{item.lineItemName}</span>
          <span className="text-muted-foreground block text-[11px]">
            {[item.projectName, item.contractCode]
              .filter((part) => part !== null && part !== '')
              .join(' · ')}
          </span>
        </span>
      </span>
      <span className="-mx-2.5 my-1.5 block border-t" />
      <span className="flex flex-col gap-1">
        {rows.map((row) => (
          <span key={row.label} className="flex items-baseline justify-between gap-3">
            <span className="text-muted-foreground">{row.label}</span>
            <span className="text-right tabular-nums">{row.value}</span>
          </span>
        ))}
      </span>
    </span>
  );

  const contract = item.contractIdentifier;
  const lineItem = item.lineItemIdentifier;
  const link = contract !== null && lineItem !== null;

  return (
    <Tooltip content={tip} delay={150} width={260} className="flex w-full min-w-0">
      <button
        type="button"
        disabled={!link}
        aria-label={`${item.lineItemName} · ${fmt(Number(item.hours))}`}
        className="hover:bg-muted/60 focus-visible:ring-ring/40 flex w-full min-w-0 items-center gap-1.5 rounded-md px-1.5 py-1 text-left transition-colors outline-none focus-visible:ring-2 disabled:cursor-default"
        onClick={() => {
          if (link) onOpenLineItem(contract, lineItem);
        }}
      >
        <span
          aria-hidden
          className="size-2 shrink-0 rounded-[3px]"
          style={{ background: item.projectColor ?? 'var(--muted-foreground)' }}
        />
        <span className="min-w-0 flex-1 truncate text-[11.5px]">{item.lineItemName}</span>
        <span className="shrink-0 text-[11px] tabular-nums">{fmt(Number(item.hours))}</span>
      </button>
    </Tooltip>
  );
}
